import { useMemo, useState } from 'react';
import { Box, useMediaQuery, useTheme, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Panel } from './Panel';
import { Selection } from './orgNavTypes';
import { useOrgFilters } from './useOrgFilters';
import { OrgTable } from './Tables/OrgTable';
import { SystemTable } from './Tables/SystemTable/SystemTable';
import { EmployeesTable } from './Tables/EmployeesTable';
import type { SystemFilter } from '@/types/systems';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { SystemAssigned } from '@consts/systems';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { FiltersDrawer } from './FiltersDrawer';
import { colors, typography as typographyTokens, radii, Button } from '@components/design-system';

/**
 * Table with tabs for each systems, locations, resellers, companies
 *
 * Org entities only use main org filters, for orgs since all of the data already exists locally, we should use a fake pagination of 30 elements per page
 * Systems use main org filters + extra filters, they do not exist locally so actual pagination is required, should use same pagination # as Org
 *
 * Due to this difference in behavior, we should split this into 2 seperate components:
 * - OrgTable
 * - SystemTable
 *
 * All behavior which describes the datatable + data loading should be encapsulated here.
 * The main org filters are shared, so should be passed down as props to both components.
 */

export type { FilterInput } from './orgNavTypes';

// Selection tab name mapping for mobile header
const TAB_NAMES: Record<Selection, string> = {
    [Selection.Company]: 'Companies',
    [Selection.Reseller]: 'Resellers',
    [Selection.Location]: 'Locations',
    [Selection.Employee]: 'Employees',
    [Selection.System]: 'Systems',
    [Selection.SystemSettings]: 'Systems',
};

export const OrgNav = () => {
    // Mobile detection
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    // All OrgNav URL state (tab, filters, type/id selection) comes from one hook
    // so browser back/forward restores it and writes never clobber each other.
    const {
        selectedTab,
        setSelectedTab,
        orgFilters,
        setOrgFilter,
        setFilters,
        clearOrgFilters,
        selection,
        openDetail,
        clearSelection,
    } = useOrgFilters();

    const [systemFilters, setSystemFilters] = useState<SystemFilter>({
        model: {},
        status: {},
        appVersion: {},
    });

    const [assigned, setAssigned] = useState(SystemAssigned.Assigned);

    // 'active' = only active, 'archived' = only archived
    const [locationStatus, setLocationStatus] = useState<'active' | 'archived'>('active');

    const [deleteEmployee, setDeleteEmployee] = useState<any>(null);

    const [searchTerm, setSearchTerm] = useLocalStorage<string>({
        key: 'systems_table_search_term',
        value: '',
    });

    const setSystemFilter = (key: keyof SystemFilter, value: string, selectOne?: boolean) => {
        const isActive = systemFilters[key][value];
        setSystemFilters({ ...systemFilters, [key]: { ...(selectOne ? {} : systemFilters[key]), [value]: !isActive } });
    };

    const filterParams = useMemo(() => {
        const { companies, resellers, locations } = orgFilters;
        const companyIds = Object.keys(companies)
            .filter((key) => companies[key])
            .join(',');
        const resellerIds = Object.keys(resellers)
            .filter((key) => resellers[key])
            .join(',');
        const locationIds = Object.keys(locations)
            .filter((key) => locations[key])
            .join(',');
        return {
            companyIds,
            resellerIds,
            locationIds,
            includeInactive: locationStatus === 'archived' ? 'only' : 'false',
        };
    }, [orgFilters, locationStatus]);

    const handleSystemClear = () => {
        setSystemFilters({
            model: {},
            status: {},
            appVersion: {},
        });
        setSearchTerm('');
        setAssigned(SystemAssigned.Assigned);
    };

    return (
        <>
            <BreadcrumbSubBar />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                }}>
                {/* Mobile Header Bar */}
                {isMobile && !selection.type && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                            flexShrink: 0,
                        }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.lg,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[900],
                            }}>
                            {TAB_NAMES[selectedTab]}
                        </Typography>
                        <Button
                            variant='secondary'
                            size='small'
                            onClick={() => setMobileDrawerOpen(true)}
                            startIcon={<FilterListIcon />}
                            sx={{
                                minWidth: 'auto',
                                px: 1,
                                '& .MuiButton-startIcon': {
                                    margin: 0,
                                },
                            }}>
                            {''}
                        </Button>
                    </Box>
                )}

                {/* Main layout container */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                    {/* Filters Panel - Hidden on mobile when selection.type is set */}
                    <FiltersDrawer
                        selectedTab={selectedTab}
                        setSelectedTab={(nextTab) => {
                            // setSelectedTab clears type/id in the same URL write,
                            // so the tab switch and detail close don't clobber.
                            setSearchTerm('');
                            setSelectedTab(nextTab);
                        }}
                        orgFilters={orgFilters}
                        setOrgFilter={setOrgFilter}
                        systemFilters={systemFilters}
                        setSystemFilter={setSystemFilter}
                        assigned={assigned}
                        setAssigned={setAssigned}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        locationStatus={locationStatus}
                        setLocationStatus={setLocationStatus}
                        onClearOrg={clearOrgFilters}
                        onClearSystem={handleSystemClear}
                        onCreate={(type) => {
                            openDetail(type, null);
                        }}
                        isMobile={isMobile}
                        mobileOpen={mobileDrawerOpen}
                        onMobileClose={() => setMobileDrawerOpen(false)}
                    />

                    {/* Main Content */}
                    <Box
                        display='flex'
                        flexDirection='column'
                        flex={1}
                        sx={{
                            minWidth: 0,
                            minHeight: 0, // Safari flex fix
                            overflowY: 'hidden',
                            overflowX: 'hidden',
                        }}>
                        {/* Data / Tables area */}
                        {!selection.type && (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 0,
                                    overflow: 'hidden',
                                    backgroundColor: '#F4F6F8',
                                }}>
                                {[Selection.Company, Selection.Reseller, Selection.Location].includes(selectedTab) && (
                                    <OrgTable
                                        // Remount on tab change so previous-tab rows can't bleed
                                        // into the new render and collide on keys.
                                        key={selectedTab}
                                        tab={selectedTab as Selection.Company | Selection.Reseller | Selection.Location}
                                        orgFilters={filterParams}
                                        onFilterChange={setFilters}
                                        isMobile={isMobile}
                                    />
                                )}

                                {selectedTab === Selection.Employee && (
                                    <EmployeesTable
                                        orgFilters={filterParams}
                                        searchText={searchTerm}
                                        onSelect={(e) => openDetail(Selection.Employee, e.EmployeeID!)}
                                        onDelete={(e) => setDeleteEmployee(e)}
                                    />
                                )}

                                {selectedTab === Selection.System && (
                                    <SystemTable
                                        assigned={assigned}
                                        filters={systemFilters}
                                        orgFilters={filterParams}
                                        searchTerm={searchTerm}
                                    />
                                )}
                            </Box>
                        )}

                        {selection.type && (
                            <Box
                                display={'flex'}
                                flexDirection={'column'}
                                width={'100%'}
                                flex={1}
                                sx={{
                                    minHeight: 0,
                                    // Safari fix: remove height: 100%, rely on flex: 1
                                    overflow: 'auto',
                                    // Ensure Safari knows this is a containing block
                                    position: 'relative',
                                }}>
                                <Panel selection={selection} onSuccess={clearSelection} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};
